/**
 * Wires the ClientErrorReporter to the API and to the browser's global error
 * hooks. Errors land in Cloud Logging on `api` as `component: "frontend"`
 * entries — see functions/src/routes/clientErrors.ts.
 *
 * On in production builds. In dev, set VITE_CLIENT_ERROR_REPORTING=true to
 * exercise it; VITE_CLIENT_ERROR_REPORTING=false turns it off anywhere.
 */

import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiRequest } from './api';
import {
  ClientErrorReporter,
  type ClientErrorLevel,
  type ClientErrorSource,
} from '../utils/clientErrorReporter';

declare const __APP_VERSION__: string | undefined;

const flag = import.meta.env.VITE_CLIENT_ERROR_REPORTING as string | undefined;
const enabled = flag === 'false' ? false : import.meta.env.PROD || flag === 'true';

function activeOrgId(): string | undefined {
  try {
    return localStorage.getItem('activeOrganizationId') || undefined;
  } catch {
    return undefined;
  }
}

let reporter: ClientErrorReporter | null = null;

function getReporter(): ClientErrorReporter | null {
  if (!enabled) return null;
  if (!reporter) {
    reporter = new ClientErrorReporter({
      send: (errors) =>
        apiRequest('/client-errors', { method: 'POST', body: JSON.stringify({ errors }) }, activeOrgId()),
      isSignedIn: () => !!auth.currentUser,
      getRoute: () => window.location.pathname,
      userAgent: navigator.userAgent,
      appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : undefined,
    });
  }
  return reporter;
}

/** Queue an error for reporting. Safe to call from anywhere; never throws. */
export function reportClientError(
  error: unknown,
  meta: { level?: ClientErrorLevel; source: ClientErrorSource }
): void {
  getReporter()?.report(error, meta);
}

let installed = false;

/** Registers window 'error' + 'unhandledrejection' listeners. Idempotent. */
export function installClientErrorReporting(): void {
  if (installed || !enabled) return;
  installed = true;

  window.addEventListener('error', (event) => {
    // Resource load failures (img/script) have no `error` and no message — skip.
    if (!event.error && !event.message) return;
    reportClientError(event.error ?? event.message, { source: 'window' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason, { source: 'unhandledrejection' });
  });

  // Errors raised before sign-in are held; send them once a session exists.
  onAuthStateChanged(auth, (user) => {
    if (user) void getReporter()?.flush();
  });

  // Best effort on tab hide, so a crash-then-close still gets through.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void getReporter()?.flush();
  });
}
